import React, { useEffect, useMemo, useState, useCallback } from "react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Switch } from "../ui/switch";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../ui/tooltip";
import {
  CheckCircle2,
  AlertCircle,
  Loader2,
  PlayCircle,
  RefreshCw,
  DollarSign,
  Layers,
  Route,
  ChevronDown,
  ChevronRight,
  ChevronUp,
  Plus,
  Pencil,
  Trash2,
  ArrowUp,
  ArrowDown,
  Info,
  BookOpen,
  Clock,
  Save,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "../ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog";

/**
 * Operational Models — Phase 1
 *
 * Routes each background operation (auto-inspect, title gen, file summary,
 * chunker, embeddings, voice STT/TTS, future tools) to a specific
 * provider+model. Cheap models for cheap tasks; expensive only where it
 * matters. Live pricing from OpenRouter, fallback assignment per op.
 */

const CATEGORY_META = {
  generative: { label: "Generative", color: "text-emerald-600 dark:text-emerald-400" },
  pipeline: { label: "Pipeline", color: "text-sky-600 dark:text-sky-400" },
  voice: { label: "Voice", color: "text-orange-600 dark:text-orange-400" },
  agentic: { label: "Agentic (future)", color: "text-purple-600 dark:text-purple-400" },
};

/**
 * Plain-English cheat sheet for each operation slug.
 * Surfaced at the top of the tab as a collapsible reference panel and
 * also as a hover tooltip next to each operation row.
 */
const OPERATION_INFO = {
  auto_inspect_summary: {
    short: "AI summary that pops up in chat right after a file is attached.",
    long: "When a user attaches a file (PDF/DocX/image/etc) the system generates the short 'what's in this document' summary that appears in the chat as a Sparkles bubble. Pick a smart-but-cheap model — Haiku, Gemini Flash, etc.",
  },
  title_generation: {
    short: "Auto-renames 'New chat' to a descriptive title after the first reply.",
    long: "After the first user message in a new conversation, this model generates a short, descriptive title (e.g. 'Tax filing questions Q3') to replace 'New chat'. Use an ultra-cheap model — this is fire-and-forget.",
  },
  file_description: {
    short: "One-line label shown next to each file in the storage panel.",
    long: "Generates the ≤100-word factual blurb shown in the storage panel tooltip and in the file row. Different from auto_inspect — this is the file-level label, not the in-chat summary.",
  },
  chunker: {
    short: "Splits extracted markdown into RAG chunks for vector search.",
    long: "Today this is `inline` (no LLM call — pure Python paragraph/header splitter, free). The slot exists in case you want to plug in a smart-chunker LLM later.",
  },
  embeddings: {
    short: "Turns each chunk into a vector for similarity search in Qdrant.",
    long: "Critical: embedding provider is locked per-file at upload, because BGE-M3 (Tensornex, 1024-dim) and OpenAI (1536-dim) can NOT be mixed in the same Qdrant collection.",
  },
  voice_stt: {
    short: "Speech-to-text for decoupled voice mode (Whisper + thinking + TTS).",
    long: "Converts the user's microphone audio into text before sending it to the thinking model. Used only in decoupled voice mode (NOT the OpenAI Realtime API path). Default: Whisper-1; Tensornex transcribe-diarized also supported.",
  },
  voice_thinking: {
    short: "The 'brain' model for decoupled voice replies.",
    long: "Takes the STT output + conversation context and produces the spoken reply text, which is then sent to voice_tts. Usually the same as the user's chat model, but can be swapped for a faster model for snappier voice.",
  },
  voice_tts: {
    short: "Text-to-speech that voices the thinking model's reply.",
    long: "Reads the thinking model's reply aloud. Default: OpenAI TTS-1; upgrade to TTS-1-HD for studio quality at higher cost.",
  },
  tool_routing: {
    short: "Future: decides which tool to call before the expensive primary model fires.",
    long: "Placeholder for MCP / tool-use agentic chat (step-bubble UI à la Claude / Emergent). When wired, a cheap model will decide 'should I call a tool? which one?' before invoking the expensive primary. Not active yet.",
  },
  email_compose: {
    short: "Future: drafts user-requested emails on a prose-tuned cheaper model.",
    long: "Placeholder. When a user asks 'draft me an email about X', the system will route to a cheaper prose-tuned model instead of burning Sonnet/GPT-5 on the request. Not wired yet.",
  },
};

/**
 * Shared context — exposes the live model catalog so that both the main
 * Operational Models rows AND the deeper RuleEditorDialog can use the same
 * <ModelPicker> component without prop-drilling or duplicate fetches.
 */
const AvailableModelsContext = React.createContext({
  models: [],
  modelsByProvider: {},
  providersConfigured: {},
});

/**
 * Auto-derive the `strategy` slug from the provider. Strategy is now an
 * internal field — the admin only ever picks a provider+model from a
 * live dropdown. This map keeps backend compatibility unchanged.
 */
const STRATEGY_FOR_PROVIDER = {
  tensornex: "docling",
  cloudconvert: "cloudconvert_md",
  anthropic: "llm_pdf",
  openrouter: "llm_pdf",
  openai: "llm_pdf",
};

/**
 * Top-level ModelPicker. Reads the live catalog from
 * AvailableModelsContext. Used in both the main operations list and inside
 * the routing rule editor dialog.
 */
function ModelPicker({ value, fallback, onChange, "data-testid": testid }) {
  const { models, modelsByProvider, providersConfigured } = React.useContext(AvailableModelsContext);
  const composite = value?.provider && value?.model_id ? `${value.provider}::${value.model_id}` : "";
  const inCatalog = !!models.find(
    (m) => m.provider === value?.provider && m.model_id === value?.model_id,
  );
  return (
    <Select
      value={composite}
      onValueChange={(v) => {
        if (!v) return;
        const [provider, ...rest] = v.split("::");
        onChange({ provider, model_id: rest.join("::") });
      }}
    >
      <SelectTrigger className="rounded-sm font-mono text-xs h-9" data-testid={testid}>
        <SelectValue placeholder={fallback ? "(no fallback — primary only)" : "Pick a provider / model"}>
          {composite ? (
            <span className="font-mono text-xs truncate">
              {value.provider} / {value.model_id}
            </span>
          ) : null}
        </SelectValue>
      </SelectTrigger>
      <SelectContent className="max-h-80">
        {composite && !inCatalog && (
          <>
            <div className="px-2 py-1 text-[10px] uppercase tracking-wider text-amber-600 dark:text-amber-400 font-mono border-b border-border bg-amber-50 dark:bg-amber-950/20">
              current (not in catalog)
            </div>
            <SelectItem
              value={composite}
              className="font-mono text-xs text-amber-700 dark:text-amber-300"
            >
              {value.provider} / {value.model_id}
            </SelectItem>
          </>
        )}
        {Object.entries(modelsByProvider).map(([prov, list]) => (
          <React.Fragment key={prov}>
            <div className="px-2 py-1 text-[10px] uppercase tracking-wider text-muted-foreground font-mono border-t border-border bg-muted/30">
              {prov}
              {providersConfigured[prov] === false && (
                <span className="ml-2 text-amber-600 dark:text-amber-500">⚠ no key</span>
              )}
            </div>
            {list.map((m) => (
              <SelectItem
                key={`${prov}::${m.model_id}`}
                value={`${prov}::${m.model_id}`}
                className="font-mono text-xs"
                disabled={providersConfigured[prov] === false}
              >
                <div className="flex items-center justify-between gap-3 w-full">
                  <span className="truncate">{m.label}</span>
                  <PriceTag inP={m.input_cost_per_mtoken_usd} outP={m.output_cost_per_mtoken_usd} />
                </div>
              </SelectItem>
            ))}
          </React.Fragment>
        ))}
      </SelectContent>
    </Select>
  );
}

function ProviderBadge({ provider, configured }) {
  if (configured === false) {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider text-amber-600 dark:text-amber-400">
        <AlertCircle className="h-3 w-3" /> key missing
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider text-emerald-600 dark:text-emerald-400">
      <CheckCircle2 className="h-3 w-3" /> ready
    </span>
  );
}

/**
 * Phase 0 (D2) — small badge that fetches the vendor spec for a (provider,
 * model_id) pair and shows max_batch / max_input_tokens / dim. Pure
 * transparency: read-only. Caches results in a module-level Map so we don't
 * re-fetch every render.
 */
const _specCache = new Map();
function SpecBadge({ provider, modelId }) {
  const [spec, setSpec] = useState(null);
  useEffect(() => {
    if (!provider || !modelId) { setSpec(null); return; }
    const k = `${provider}::${modelId}`;
    if (_specCache.has(k)) { setSpec(_specCache.get(k)); return; }
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(`/admin/provider-specs/${encodeURIComponent(provider)}/${encodeURIComponent(modelId)}`);
        _specCache.set(k, data);
        if (alive) setSpec(data);
      } catch {
        if (alive) setSpec(null);
      }
    })();
    return () => { alive = false; };
  }, [provider, modelId]);
  if (!spec || !spec.spec) return null;
  const s = spec.spec;
  const parts = [];
  if (s.max_batch && s.max_batch > 1) parts.push(`batch ${s.max_batch}`);
  if (s.max_input_tokens && s.max_input_tokens > 0) parts.push(`in ${s.max_input_tokens.toLocaleString()} tok`);
  if (s.dim) parts.push(`${s.dim}-dim`);
  if (s.max_pdf_pages) parts.push(`≤${s.max_pdf_pages} pp`);
  if (s.default_timeout_seconds) parts.push(`${Math.round(s.default_timeout_seconds / 60)} min`);
  if (parts.length === 0) return null;
  return (
    <div
      className="mt-1 text-[10px] font-mono text-muted-foreground tracking-tight"
      data-testid={`op-spec-${provider}-${modelId}`}
      title={`Spec source: ${spec.source}`}
    >
      {parts.join(" · ")}
    </div>
  );
}

function PriceTag({ inP, outP }) {
  if (!inP && !outP) return <span className="text-xs text-muted-foreground font-mono">—</span>;
  return (
    <span className="text-xs font-mono text-muted-foreground">
      ${(+inP).toFixed(2)} / ${(+outP).toFixed(2)} /M
    </span>
  );
}


// ===========================================================================
// Phase 3.3 — Fallback chain editor (up to 4 entries per row)
// ===========================================================================
//
// Renders the ordered list of fallback {provider, model_id} entries for an
// operational row. Each entry has:
//   * ModelPicker — admin picks the provider/model.
//   * Spec badge underneath showing batch / input tokens / dim.
//   * Per-entry "Remove" button.
//   * (Embeddings row only) compatibility chip — green if dim matches the
//     primary's dim, yellow "cross-dim — locked users skip" otherwise.
//
// "+ Add fallback" appends a new empty slot until the cap is reached. State
// is local to this component and bubbles up to the parent via `onChange`
// whenever the user picks a model or removes a slot — so we never send a
// half-empty {provider:"x", model_id:""} entry to the backend.

const MAX_FALLBACK_CHAIN_LEN = 4;

function dimFromCache(provider, modelId) {
  if (!provider || !modelId) return null;
  const k = `${provider}::${modelId}`;
  const entry = _specCache.get(k);
  return entry?.spec?.dim ?? null;
}

function FallbackChainEditor({ op, onChange }) {
  const incoming = Array.isArray(op?.fallback_chain) ? op.fallback_chain : [];
  // Local draft so an in-flight "+ Add fallback" doesn't immediately PUT
  // an empty slot.
  const [draft, setDraft] = useState(incoming);
  // Tick state to re-render once the SpecBadge components populate the
  // shared _specCache for newly-added rows.
  const [tick, setTick] = useState(0);
  // Resync when the parent reloads after a save.
  React.useEffect(() => { setDraft(incoming); /* eslint-disable-next-line */ }, [JSON.stringify(incoming)]);
  // Bump tick on a short interval so compatibility chips refresh once the
  // spec endpoint resolves (cheap; only runs while the row is mounted).
  React.useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 800);
    return () => clearInterval(id);
  }, []);

  const primaryDim = dimFromCache(op?.provider, op?.model_id);
  const isEmbeddings = op?.operation_slug === "embeddings";

  const updateEntry = (idx, next) => {
    const updated = [...draft];
    updated[idx] = { provider: next.provider || "", model_id: next.model_id || "" };
    setDraft(updated);
    const validEntries = updated.filter((e) => e.provider && e.model_id);
    onChange(validEntries);
  };

  const removeEntry = (idx) => {
    const updated = draft.filter((_, i) => i !== idx);
    setDraft(updated);
    onChange(updated);
  };

  const addSlot = () => {
    if (draft.length >= MAX_FALLBACK_CHAIN_LEN) return;
    setDraft([...draft, { provider: "", model_id: "" }]);
  };

  return (
    <div className="space-y-2" data-testid={`fallback-chain-${op.operation_slug}`}>
      {draft.length === 0 && (
        <div className="text-[11px] italic text-muted-foreground">
          No fallback configured — primary failure will error the request.
        </div>
      )}
      {draft.map((entry, idx) => {
        const entryDim = dimFromCache(entry.provider, entry.model_id);
        const compatible = isEmbeddings && primaryDim && entryDim
          ? primaryDim === entryDim
          : null;
        return (
          <div
            key={`${op.operation_slug}-fb-${idx}-${tick > 9999 ? 0 : ""}`}
            className="border border-border bg-muted/30 rounded-sm p-2"
            data-testid={`fallback-chain-${op.operation_slug}-entry-${idx}`}
          >
            <div className="flex items-center justify-between mb-1.5">
              <div className="text-[10px] uppercase tracking-wider font-mono text-muted-foreground">
                Fallback #{idx + 1}
              </div>
              <button
                type="button"
                onClick={() => removeEntry(idx)}
                className="text-[11px] text-rose-600 hover:underline"
                data-testid={`fallback-chain-${op.operation_slug}-remove-${idx}`}
              >
                Remove
              </button>
            </div>
            <ModelPicker
              data-testid={`op-fallback-${op.operation_slug}-${idx}`}
              value={{ provider: entry.provider, model_id: entry.model_id }}
              fallback
              onChange={(v) => updateEntry(idx, v)}
            />
            <SpecBadge provider={entry.provider} modelId={entry.model_id} />
            {isEmbeddings && entry.provider && entry.model_id && compatible !== null && (
              <div
                className={`mt-1 text-[10px] font-mono ${compatible
                  ? "text-green-700 dark:text-green-300"
                  : "text-amber-700 dark:text-amber-300"}`}
                data-testid={`fallback-chain-${op.operation_slug}-compat-${idx}`}
              >
                {compatible
                  ? "✓ Same dim — usable for any user"
                  : "⚠ Cross-dim — locked users will skip; new users may end up here"}
              </div>
            )}
          </div>
        );
      })}
      {draft.length < MAX_FALLBACK_CHAIN_LEN && (
        <button
          type="button"
          onClick={addSlot}
          className="text-xs font-mono text-sky-700 dark:text-sky-300 hover:underline"
          data-testid={`fallback-chain-${op.operation_slug}-add`}
        >
          + Add fallback {draft.length > 0 ? `(${draft.length}/${MAX_FALLBACK_CHAIN_LEN})` : ""}
        </button>
      )}
    </div>
  );
}

export default function OperationalModelsTab() {
  const [ops, setOps] = useState([]);
  const [providersConfigured, setProvidersConfigured] = useState({});
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingSlug, setSavingSlug] = useState(null);
  const [testingSlug, setTestingSlug] = useState(null);
  const [testResults, setTestResults] = useState({});
  const [refreshingPrices, setRefreshingPrices] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [a, b] = await Promise.all([
        api.get("/admin/operational-models"),
        api.get("/admin/operational-models/available-models"),
      ]);
      setOps(a.data.operations || []);
      setProvidersConfigured(a.data.providers_configured || {});
      setModels(b.data.models || []);
    } catch (e) {
      toast.error("Failed to load operational models");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, []);

  const refreshPrices = async () => {
    setRefreshingPrices(true);
    try {
      const { data } = await api.get("/admin/operational-models/available-models");
      setModels(data.models || []);
      toast.success(`Refreshed ${data.models.length} models`);
    } catch {
      toast.error("Price refresh failed");
    } finally {
      setRefreshingPrices(false);
    }
  };

  /** Models grouped by provider — for dropdown sections. */
  const modelsByProvider = useMemo(() => {
    const grouped = {};
    for (const m of models) {
      (grouped[m.provider] = grouped[m.provider] || []).push(m);
    }
    return grouped;
  }, [models]);

  const patchOp = async (slug, patch) => {
    setSavingSlug(slug);
    try {
      const { data } = await api.put(`/admin/operational-models/${slug}`, patch);
      setOps((cur) => cur.map((o) => (o.operation_slug === slug ? data : o)));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSavingSlug(null);
    }
  };

  // ─── Phase 2 (B4) ──────────────────────────────────────────────────────
  // Per-user embedding lock: when an admin changes the PRIMARY of the
  // `embeddings` operational row, existing users keep using their old model
  // (the lock pins them). Before the save lands, we probe the backend for
  // the count of locked-elsewhere users and show a confirm modal.
  // Pure UX — the backend lock logic enforces correctness regardless.
  const [embedConfirm, setEmbedConfirm] = useState(null);
  // Shape: {slug, patch, impact: {users_locked_to_different_model, ...}, applying}

  // ─── Phase 3.2 ─────────────────────────────────────────────────────────
  // Stranded-user tile: shows how many users are locked to an embedding
  // model that no longer matches admin's current primary. Clicking opens
  // a modal listing them so admin can jump to each Danger Zone page.
  const [stranded, setStranded] = useState(null);
  const [strandedOpen, setStrandedOpen] = useState(false);

  const loadStranded = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/embeddings/stranded");
      setStranded(data);
    } catch {/* non-fatal */}
  }, []);

  useEffect(() => { loadStranded(); }, [loadStranded, ops]);

  const requestPatchOp = async (slug, patch) => {
    const isEmbeddingsPrimaryChange =
      slug === "embeddings" &&
      (patch.provider != null || patch.model_id != null);
    if (!isEmbeddingsPrimaryChange) {
      return patchOp(slug, patch);
    }
    const op = ops.find((o) => o.operation_slug === slug);
    const newProvider = patch.provider ?? op?.provider;
    const newModelId = patch.model_id ?? op?.model_id;
    if (!newProvider || !newModelId) {
      return patchOp(slug, patch);
    }
    // If nothing actually changed, just save (no modal).
    if (newProvider === op?.provider && newModelId === op?.model_id) {
      return patchOp(slug, patch);
    }
    try {
      const { data } = await api.get(
        `/admin/embeddings/save-impact?provider=${encodeURIComponent(newProvider)}&model_id=${encodeURIComponent(newModelId)}`,
      );
      const lockedElsewhere = data?.users_locked_to_different_model || 0;
      if (lockedElsewhere === 0) {
        // Nothing to warn about — proceed silently.
        return patchOp(slug, patch);
      }
      setEmbedConfirm({ slug, patch, impact: data, applying: false });
    } catch {
      // If the impact probe fails, fall through to a save (the backend
      // claim logic still protects correctness).
      return patchOp(slug, patch);
    }
  };

  const confirmEmbedSave = async () => {
    if (!embedConfirm) return;
    setEmbedConfirm((c) => ({ ...c, applying: true }));
    await patchOp(embedConfirm.slug, embedConfirm.patch);
    setEmbedConfirm(null);
  };

  const runTest = async (slug) => {
    setTestingSlug(slug);
    setTestResults((r) => ({ ...r, [slug]: null }));
    try {
      // Phase 5.2 — backend now pings primary + EVERY entry in the
      // fallback_chain. Response: {primary: {...}, chain: [{...}, {...}, ...]}.
      const { data } = await api.post(`/admin/operational-models/${slug}/test?which=all`);
      setTestResults((r) => ({ ...r, [slug]: data }));
      const primOk = data?.primary?.ok;
      const chain = Array.isArray(data?.chain) ? data.chain : [];
      const okCount = (primOk ? 1 : 0) + chain.filter((c) => c.ok).length;
      const totalCount = 1 + chain.length;
      if (okCount === totalCount) {
        toast.success(`${slug}: all ${totalCount} target(s) passed`);
      } else if (okCount === 0) {
        toast.error(`${slug}: all ${totalCount} target(s) failed`);
      } else {
        toast.warning(`${slug}: ${okCount}/${totalCount} target(s) passed`);
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message;
      setTestResults((r) => ({ ...r, [slug]: { primary: { ok: false, error: msg }, chain: [] } }));
      toast.error(`${slug}: ${msg}`);
    } finally {
      setTestingSlug(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20 text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin mr-2" />
        Loading operational models...
      </div>
    );
  }

  return (
    <TooltipProvider delayDuration={150}>
    <AvailableModelsContext.Provider value={{ models, modelsByProvider, providersConfigured }}><div className="space-y-8" data-testid="operational-models-tab">
      {/* ===== HEADER ===== */}
      <div className="flex items-end justify-between gap-4 border-b border-border pb-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground font-mono">
            [ behind-the-scenes routing ]
          </div>
          <h2 className="font-display font-black tracking-tight text-2xl mt-2">
            Operational Models
          </h2>
          <p className="text-sm text-muted-foreground mt-1.5 max-w-2xl">
            Route each background task to its own provider+model. Users still see
            their chat model — these run invisibly. Saves cost on cheap tasks
            (titles) while keeping quality where it matters (auto-inspect summaries).
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={refreshPrices}
          disabled={refreshingPrices}
          className="rounded-sm font-mono text-xs"
          data-testid="refresh-pricing-btn"
        >
          {refreshingPrices ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <RefreshCw className="h-3.5 w-3.5 mr-1.5" />}
          Refresh pricing
        </Button>
      </div>

      {/* ===== CHEAT SHEET PANEL ===== */}
      <CheatSheetPanel ops={ops} />

      {/* ===== OPERATIONS LIST ===== */}
      <div className="space-y-px bg-border border border-border">
        {ops.map((op) => {
          const cat = CATEGORY_META[op.category] || CATEGORY_META.generative;
          const tr = testResults[op.operation_slug];
          const dirty = savingSlug === op.operation_slug;
          return (
            <div
              key={op.operation_slug}
              className="bg-card p-5"
              data-testid={`op-row-${op.operation_slug}`}
            >
              <div className="grid grid-cols-12 gap-4 items-start">
                {/* Label + category */}
                <div className="col-span-12 lg:col-span-3">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Layers className={`h-3.5 w-3.5 ${cat.color}`} />
                    <span className={`text-[10px] uppercase tracking-wider font-mono ${cat.color}`}>
                      {cat.label}
                    </span>
                    {!op.enabled && (
                      <span className="text-[10px] uppercase tracking-wider font-mono text-muted-foreground">
                        · disabled
                      </span>
                    )}
                  </div>
                  <div className="font-display font-bold tracking-tight mt-1.5 flex items-center gap-1.5">
                    <span>{op.label}</span>
                    {(OPERATION_INFO[op.operation_slug] || op.tooltip) && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button
                            type="button"
                            className="text-muted-foreground hover:text-foreground transition-colors"
                            aria-label={`What is ${op.label}?`}
                            data-testid={`op-info-${op.operation_slug}`}
                          >
                            <Info className="h-3.5 w-3.5" />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent side="right" className="max-w-xs text-xs leading-relaxed">
                          {OPERATION_INFO[op.operation_slug]?.long || op.tooltip}
                        </TooltipContent>
                      </Tooltip>
                    )}
                  </div>
                  <div className="text-[10px] font-mono text-muted-foreground mt-0.5">
                    {op.operation_slug}
                  </div>
                  {op.operation_slug === "embeddings" && stranded?.stranded_count > 0 && (
                    <button
                      type="button"
                      onClick={() => setStrandedOpen(true)}
                      className="mt-1.5 inline-flex items-center gap-1 text-[11px] font-mono text-amber-700 dark:text-amber-300 hover:underline"
                      data-testid="stranded-tile"
                    >
                      ⚠ {stranded.stranded_count} user{stranded.stranded_count === 1 ? "" : "s"} stranded · View →
                    </button>
                  )}
                </div>

                {/* Primary picker */}
                <div className="col-span-12 lg:col-span-3">
                  <div className="text-[10px] uppercase tracking-wider font-mono text-muted-foreground mb-1.5">
                    Primary
                  </div>
                  <ModelPicker
                    data-testid={`op-primary-${op.operation_slug}`}
                    value={{ provider: op.provider, model_id: op.model_id }}
                    onChange={(v) => requestPatchOp(op.operation_slug, v)}
                  />
                  <div className="mt-1">
                    <ProviderBadge
                      provider={op.provider}
                      configured={providersConfigured[op.provider]}
                    />
                  </div>
                  <SpecBadge provider={op.provider} modelId={op.model_id} />
                </div>

                {/* Phase 3.3 — Fallback chain (up to 4) */}
                <div className="col-span-12 lg:col-span-3">
                  <div className="text-[10px] uppercase tracking-wider font-mono text-muted-foreground mb-1.5">
                    Fallback chain (on failure / budget cap)
                  </div>
                  <FallbackChainEditor
                    op={op}
                    onChange={(chain) =>
                      patchOp(op.operation_slug, { fallback_chain: chain })
                    }
                  />
                </div>

                {/* Markup + Budget + Toggle */}
                <div className="col-span-12 lg:col-span-3 space-y-2">
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <div className="text-[10px] uppercase tracking-wider font-mono text-muted-foreground mb-1.5">
                        Markup %
                      </div>
                      <Input
                        type="number"
                        defaultValue={op.markup_pct ?? ""}
                        className="rounded-sm h-9 font-mono text-xs"
                        data-testid={`op-markup-${op.operation_slug}`}
                        onBlur={(e) => {
                          const v = e.target.value === "" ? null : parseFloat(e.target.value);
                          if (v !== op.markup_pct)
                            patchOp(op.operation_slug, { markup_pct: v });
                        }}
                      />
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-wider font-mono text-muted-foreground mb-1.5">
                        Budget $/mo
                      </div>
                      <Input
                        type="number"
                        defaultValue={op.budget_cap_usd ?? ""}
                        className="rounded-sm h-9 font-mono text-xs"
                        data-testid={`op-budget-${op.operation_slug}`}
                        onBlur={(e) => {
                          const v = e.target.value === "" ? null : parseFloat(e.target.value);
                          if (v !== op.budget_cap_usd)
                            patchOp(op.operation_slug, { budget_cap_usd: v });
                        }}
                      />
                    </div>
                  </div>
                  <div className="flex items-center justify-between gap-2 pt-1">
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={op.enabled}
                        onCheckedChange={(v) =>
                          patchOp(op.operation_slug, { enabled: v })
                        }
                        data-testid={`op-enabled-${op.operation_slug}`}
                      />
                      <span className="text-xs font-mono text-muted-foreground">
                        {op.enabled ? "ON" : "OFF"}
                      </span>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => runTest(op.operation_slug)}
                      disabled={testingSlug === op.operation_slug}
                      className="rounded-sm font-mono text-xs h-9"
                      data-testid={`op-test-${op.operation_slug}`}
                    >
                      {testingSlug === op.operation_slug ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <PlayCircle className="h-3.5 w-3.5" />
                      )}
                      <span className="ml-1.5">Test</span>
                    </Button>
                  </div>
                </div>
              </div>

              {/* Test results — show both primary and fallback */}
              {tr && (
                <div className="mt-3 space-y-2" data-testid={`op-test-result-${op.operation_slug}`}>
                  {/* Phase 5.2 — render primary + every fallback in the chain.
                      Backend returns {primary: {...}, chain: [{...}, ...]}.
                      Falls back to legacy {primary, fallback} shape for old
                      response payloads still cached in state. */}
                  {(() => {
                    const slots = [];
                    if (tr.primary) slots.push({ label: "primary", result: tr.primary });
                    if (Array.isArray(tr.chain)) {
                      tr.chain.forEach((r, i) => slots.push({ label: `fallback #${i + 1}`, result: r }));
                    } else if (tr.fallback) {
                      slots.push({ label: "fallback", result: tr.fallback });
                    }
                    return slots.map(({ label, result: r }, idx) => {
                      if (!r) return null;
                      if (r.skipped) {
                        return (
                          <div
                            key={`${label}-${idx}`}
                            className="p-2.5 rounded-sm border border-border bg-muted/30"
                            data-testid={`op-test-${label.replace(/[ #]/g, "")}-${op.operation_slug}`}
                          >
                            <div className="flex items-center gap-2 text-xs font-mono text-muted-foreground">
                              <span className="uppercase tracking-wider text-[10px] font-bold">{label}</span>
                              <span>·</span>
                              <span>not configured (skipped)</span>
                            </div>
                          </div>
                        );
                      }
                      return (
                        <div
                          key={`${label}-${idx}`}
                          className={`p-2.5 rounded-sm border ${
                            r.ok
                              ? "border-emerald-300 bg-emerald-50 dark:border-emerald-700/60 dark:bg-emerald-950/30"
                              : "border-red-300 bg-red-50 dark:border-red-700/60 dark:bg-red-950/30"
                          }`}
                          data-testid={`op-test-${label.replace(/[ #]/g, "")}-${op.operation_slug}`}
                        >
                          <div className="flex items-center gap-2 text-xs font-mono flex-wrap">
                            {r.ok ? (
                              <CheckCircle2 className="h-3.5 w-3.5 text-emerald-700 dark:text-emerald-400 shrink-0" />
                            ) : (
                              <AlertCircle className="h-3.5 w-3.5 text-red-700 dark:text-red-400 shrink-0" />
                            )}
                            <span className="uppercase tracking-wider text-[10px] font-bold text-foreground">{label}</span>
                            <span className="text-muted-foreground">·</span>
                            {r.provider && (
                              <>
                                <span className="text-foreground">{r.provider}</span>
                                {r.model_id && (
                                  <span className="text-muted-foreground">/ {r.model_id}</span>
                                )}
                                <span className="text-muted-foreground">·</span>
                              </>
                            )}
                            {r.ok ? (
                              <>
                                <span className="text-emerald-700 dark:text-emerald-300 font-bold">OK</span>
                                <span className="text-muted-foreground">·</span>
                                <span className="text-foreground">{r.latency_ms}ms</span>
                                {r.sample && (
                                  <>
                                    <span className="text-muted-foreground">·</span>
                                    <span className="text-muted-foreground truncate">"{r.sample}"</span>
                                  </>
                                )}
                              </>
                            ) : (
                              <span className="text-red-700 dark:text-red-300 break-all">{r.error}</span>
                            )}
                          </div>
                        </div>
                      );
                    });
                  })()}
                </div>
              )}

              {dirty && (
                <div className="mt-2 text-[10px] font-mono text-muted-foreground flex items-center gap-1.5">
                  <Loader2 className="h-3 w-3 animate-spin" /> saving…
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* ===== FOOTER NOTE ===== */}
      <div className="border-t border-border pt-4 text-xs text-muted-foreground font-mono leading-relaxed space-y-1">
        <div className="flex items-center gap-1.5">
          <DollarSign className="h-3 w-3" /> Prices shown are per million tokens (input / output).
        </div>
        <div>
          • <strong>user_choice</strong> means the operation uses whatever chat model the user has selected.
        </div>
        <div>
          • <strong>inline</strong> is our built-in algorithmic chunker (no API call, free).
        </div>
        <div>
          • Operations marked <strong>disabled</strong> are skipped (no-op) — use this to turn off auto-titling, for example.
        </div>
      </div>

      {/* ===== ROUTING RULES SECTION (Phase 2) ===== */}
      <RoutingRulesSection />

      {/* ===== PROVIDER SETTINGS SECTION (Phase 7 — D3) ===== */}
      <ProviderSettingsSection />

      {/* ===== CHUNKING PROFILES SECTION (Phase 5) ===== */}
      <ChunkingProfilesSection />

      {/* Phase 3.2 — Stranded-users dialog (list + jump-to-danger-zone) */}
      <Dialog open={strandedOpen} onOpenChange={setStrandedOpen}>
        <DialogContent data-testid="stranded-users-dialog" className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Stranded users — locked to a different embedding model</DialogTitle>
            <DialogDescription asChild>
              <div className="text-sm text-muted-foreground space-y-2">
                <p>
                  These users were locked to an embedding model before you changed the
                  default. They'll keep using their old model until you (or they) trigger
                  a re-embed. Click a user to open their Danger Zone.
                </p>
                <p className="text-xs">
                  Current default:{" "}
                  <code className="font-mono text-foreground">
                    {stranded?.current_primary?.provider}/{stranded?.current_primary?.model_id}
                  </code>
                </p>
              </div>
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-96 overflow-y-auto space-y-2">
            {(stranded?.by_locked_model || []).map((grp) => (
              <div key={`${grp.provider}/${grp.model_id}`} className="border border-border rounded-sm p-3">
                <div className="text-xs font-mono mb-1.5">
                  <span className="text-amber-700 dark:text-amber-300">Locked to:</span>{" "}
                  <span className="text-foreground">{grp.provider}/{grp.model_id}</span>
                  <span className="text-muted-foreground">  ·  {grp.user_count} user{grp.user_count === 1 ? "" : "s"}</span>
                </div>
                <ul className="space-y-1">
                  {(grp.user_ids || []).map((uid, idx) => (
                    <li key={uid} className="text-sm font-mono flex items-center justify-between gap-2">
                      <span className="truncate text-muted-foreground">
                        {(grp.emails || [])[idx] || uid}
                      </span>
                      <a
                        href={`/admin/users/${uid}`}
                        className="text-xs text-sky-700 dark:text-sky-300 hover:underline shrink-0"
                        data-testid={`stranded-user-${uid}`}
                      >
                        Open →
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
            {(!stranded?.by_locked_model || stranded.by_locked_model.length === 0) && (
              <div className="text-sm text-muted-foreground italic">No stranded users.</div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setStrandedOpen(false)} data-testid="stranded-dialog-close">
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Phase 2 (B4) — embeddings change confirmation modal */}
      <AlertDialog open={!!embedConfirm} onOpenChange={(o) => !o && !embedConfirm?.applying && setEmbedConfirm(null)}>
        <AlertDialogContent data-testid="embedding-change-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Change vector embedding model?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="text-sm space-y-3 text-left">
                <div>
                  You're switching the embedding primary to{" "}
                  <code className="font-mono text-foreground">{embedConfirm?.impact?.new_provider}/{embedConfirm?.impact?.new_model_id}</code>.
                </div>
                <div className="border border-amber-500/40 bg-amber-500/10 rounded-sm p-3 font-mono text-xs space-y-1">
                  <div data-testid="embed-impact-different">
                    <strong>{embedConfirm?.impact?.users_locked_to_different_model || 0}</strong> existing users are <strong>locked</strong> to a different embedding model and will <strong>keep</strong> using their current one (their Qdrant vectors are dimension-pinned).
                  </div>
                  <div data-testid="embed-impact-same">
                    <strong>{embedConfirm?.impact?.users_locked_to_same_model || 0}</strong> users are already locked to this same model.
                  </div>
                  <div data-testid="embed-impact-unlocked">
                    <strong>{embedConfirm?.impact?.users_unlocked || 0}</strong> users are unlocked — they'll adopt this new model on their next upload.
                  </div>
                </div>
                <div className="text-xs text-muted-foreground">
                  To force a locked user onto this new model, open their User Detail page → Danger zone → <em>Re-embed user</em>.
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={embedConfirm?.applying} data-testid="embed-change-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); confirmEmbedSave(); }}
              disabled={embedConfirm?.applying}
              data-testid="embed-change-confirm"
            >
              {embedConfirm?.applying ? (
                <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />Saving…</>
              ) : (
                "Save anyway"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
    </AvailableModelsContext.Provider>
    </TooltipProvider>
  );
}


/**
 * Phase 2 — Extraction Routing Rules. Each rule matches a file's
 * (type + size + page count) and picks a primary extraction strategy plus
 * an optional speculative fallback. The simulator at the bottom lets admin
 * preview which rule any hypothetical file would hit.
 */

/**
 * Phase 5 — Chunking Profiles
 * ----------------------------
 * Lets the admin override min/target/max/overlap per profile. The smart
 * chunker (services/smart_chunker.py) classifies each uploaded document
 * into one of these profiles automatically (banner→short_form, contract
 * →legal, etc.) and uses the matching sizing knobs for that doc.
 */
function ChunkingProfilesSection() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [savingName, setSavingName] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRow, setEditingRow] = useState(null); // null = create mode
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewHint, setPreviewHint] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/chunking-profiles");
      setRows(data.profiles || []);
    } catch (e) {
      toast.error("Failed to load chunking profiles");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const saveSizing = async (row) => {
    // Quick-save the 4 size numbers only (inline editor).
    setSavingName(row.name);
    try {
      await api.put(`/admin/chunking-profiles/${row.name}`, {
        min_size: row.min_size,
        target_size: row.target_size,
        max_size: row.max_size,
        overlap: row.overlap,
      });
      toast.success(`${row.name} updated`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSavingName(null);
    }
  };

  const saveFromDialog = async (payload, isEdit) => {
    setSavingName(payload.name);
    try {
      if (isEdit) {
        await api.put(`/admin/chunking-profiles/${payload.name}`, payload);
        toast.success(`"${payload.name}" updated`);
      } else {
        await api.post("/admin/chunking-profiles", payload);
        toast.success(`"${payload.name}" created`);
      }
      setDialogOpen(false);
      setEditingRow(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSavingName(null);
    }
  };

  const resetRow = async (name) => {
    if (!window.confirm(`Reset "${name}" profile to factory defaults?`)) return;
    setSavingName(name);
    try {
      await api.post(`/admin/chunking-profiles/${name}/reset`);
      toast.success(`${name} reset to defaults`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reset failed");
    } finally {
      setSavingName(null);
    }
  };

  const deleteRow = async (name) => {
    if (!window.confirm(`Delete custom profile "${name}"? This cannot be undone.`)) return;
    setSavingName(name);
    try {
      await api.delete(`/admin/chunking-profiles/${name}`);
      toast.success(`"${name}" deleted`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    } finally {
      setSavingName(null);
    }
  };

  const builtInCount = rows.filter((r) => r.built_in).length;
  const customCount = rows.length - builtInCount;

  return (
    <div className="mt-8 border-t border-border pt-6" data-testid="chunking-profiles-section">
      <div className="flex items-center justify-between mb-3">
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="flex items-center gap-2 hover:opacity-80 transition-opacity"
          data-testid="chunking-profiles-toggle"
        >
          {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          <h3 className="text-sm font-bold uppercase tracking-wider font-mono">
            Chunking profiles
          </h3>
          <span className="text-xs font-mono text-muted-foreground">
            ({customCount} custom · {builtInCount} built-in)
          </span>
        </button>
        {expanded && (
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              className="h-7 rounded-sm text-[11px]"
              onClick={() => { setPreviewHint(null); setPreviewOpen(true); }}
              data-testid="chunking-profile-try-classifier"
            >
              Try classifier
            </Button>
            <Button
              size="sm"
              className="h-7 rounded-sm text-[11px]"
              onClick={() => { setEditingRow(null); setDialogOpen(true); }}
              data-testid="chunking-profile-add"
            >
              + Add document type
            </Button>
          </div>
        )}
      </div>

      {expanded && (
        <div className="space-y-3" data-testid="chunking-profiles-list">
          <div className="text-[11px] font-mono text-muted-foreground leading-relaxed">
            The smart chunker auto-detects each document's type and applies the matching sizing.
            Sizes are in tokens (cl100k_base). Built-in types can be edited or reset; custom types you create can be deleted.
            Detection uses plain-English keywords (no regex required — the system safely escapes everything you type).
          </div>

          {loading && <div className="text-xs text-muted-foreground italic">Loading…</div>}

          <div className="grid grid-cols-1 gap-2">
            {rows.map((row) => (
              <ChunkingProfileRow
                key={row.name}
                row={row}
                onSave={saveSizing}
                onReset={resetRow}
                onDelete={deleteRow}
                onEdit={() => { setEditingRow(row); setDialogOpen(true); }}
                onTest={() => { setPreviewHint(row.name); setPreviewOpen(true); }}
                saving={savingName === row.name}
              />
            ))}
          </div>
        </div>
      )}

      {dialogOpen && (
        <ChunkingProfileDialog
          row={editingRow}
          onClose={() => { setDialogOpen(false); setEditingRow(null); }}
          onSave={saveFromDialog}
          saving={savingName === (editingRow?.name)}
        />
      )}

      {previewOpen && (
        <ChunkingProfilePreviewDialog
          hint={previewHint}
          profiles={rows}
          onClose={() => { setPreviewOpen(false); setPreviewHint(null); }}
        />
      )}
    </div>
  );
}

function ChunkingProfileRow({ row, onSave, onReset, onDelete, onEdit, onTest, saving }) {
  const [draft, setDraft] = useState(row);
  React.useEffect(() => { setDraft(row); }, [row.name, row.is_default, row.target_size]); // eslint-disable-line

  const dirty =
    draft.min_size !== row.min_size ||
    draft.target_size !== row.target_size ||
    draft.max_size !== row.max_size ||
    draft.overlap !== row.overlap;

  const summary = (row.classifier_keywords || []).length > 0
    ? `${row.target_size} tok · detected by: ${row.classifier_keywords.slice(0, 5).join(", ")}${row.classifier_keywords.length > 5 ? "…" : ""} (need ${row.classifier_min_matches}+)`
    : `${row.target_size} tok · detected by document structure`;

  return (
    <div
      className={`border rounded-sm p-2.5 ${row.is_default ? "border-border bg-muted/20" : "border-amber-400/40 bg-amber-50/40 dark:bg-amber-950/10"}`}
      data-testid={`chunking-profile-${row.name}`}
    >
      <div className="flex items-center justify-between mb-1.5">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <span className="text-base">{row.built_in ? "🔒" : "⭐"}</span>
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-xs font-mono font-semibold">{row.name}</span>
              {row.built_in ? (
                <span className="text-[10px] uppercase font-mono text-muted-foreground px-1.5 py-0.5 border border-border rounded">
                  built-in
                </span>
              ) : (
                <span className="text-[10px] uppercase font-mono text-amber-700 dark:text-amber-300 px-1.5 py-0.5 border border-amber-400/40 rounded">
                  custom
                </span>
              )}
              {!row.is_default && row.built_in && (
                <span className="text-[10px] uppercase font-mono text-amber-700 dark:text-amber-300 px-1.5 py-0.5 border border-amber-400/40 rounded">
                  edited
                </span>
              )}
              {!row.enabled && (
                <span className="text-[10px] uppercase font-mono text-muted-foreground px-1.5 py-0.5 border border-border rounded">
                  disabled
                </span>
              )}
            </div>
            <div className="text-[10px] font-mono text-muted-foreground truncate">
              {row.label}
            </div>
            <div className="text-[10px] font-mono text-muted-foreground/80 truncate">
              {summary}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          <Button
            size="sm"
            variant="ghost"
            className="h-7 rounded-sm text-[11px]"
            onClick={onTest}
            disabled={saving}
            data-testid={`chunking-profile-${row.name}-test`}
          >
            Test
          </Button>
          <Button
            size="sm"
            variant="ghost"
            className="h-7 rounded-sm text-[11px]"
            onClick={onEdit}
            disabled={saving}
            data-testid={`chunking-profile-${row.name}-edit`}
          >
            Edit
          </Button>
          {row.built_in && !row.is_default && (
            <Button
              size="sm"
              variant="ghost"
              className="h-7 rounded-sm text-[11px]"
              onClick={() => onReset(row.name)}
              disabled={saving}
              data-testid={`chunking-profile-${row.name}-reset`}
            >
              Reset
            </Button>
          )}
          {!row.built_in && (
            <Button
              size="sm"
              variant="ghost"
              className="h-7 rounded-sm text-[11px] text-red-600 hover:text-red-700"
              onClick={() => onDelete(row.name)}
              disabled={saving}
              data-testid={`chunking-profile-${row.name}-delete`}
            >
              Delete
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-4 gap-2 mt-2">
        {["min_size", "target_size", "max_size", "overlap"].map((field) => (
          <div key={field}>
            <div className="text-[10px] uppercase tracking-wider font-mono text-muted-foreground mb-1">
              {field.replace("_", " ")}
            </div>
            <Input
              type="number"
              min={0}
              value={draft[field]}
              onChange={(e) =>
                setDraft({ ...draft, [field]: parseInt(e.target.value || "0", 10) })
              }
              className="rounded-sm h-8 font-mono text-xs"
              data-testid={`chunking-profile-${row.name}-${field}`}
            />
            <div className="text-[10px] font-mono text-muted-foreground mt-0.5">
              default: {row.defaults[field]}
            </div>
          </div>
        ))}
      </div>
      {dirty && (
        <div className="mt-2 flex justify-end">
          <Button
            size="sm"
            className="h-7 rounded-sm text-[11px]"
            onClick={() => onSave(draft)}
            disabled={saving}
            data-testid={`chunking-profile-${row.name}-save`}
          >
            {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : "Save sizing"}
          </Button>
        </div>
      )}
    </div>
  );
}

/**
 * Phase 5.1 — Create / edit a chunking profile.
 * For built-in profiles, the `name` field is locked; everything else is
 * editable. For custom (admin-created) profiles, `name` is required + locked
 * after creation (delete-then-recreate to rename).
 */
function ChunkingProfileDialog({ row, onClose, onSave, saving }) {
  const isEdit = !!row;
  const [draft, setDraft] = useState({
    name: row?.name || "",
    label: row?.label || "",
    min_size: row?.min_size ?? 200,
    target_size: row?.target_size ?? 800,
    max_size: row?.max_size ?? 1500,
    overlap: row?.overlap ?? 100,
    classifier_keywords_text: (row?.classifier_keywords || []).join("\n"),
    classifier_min_matches: row?.classifier_min_matches ?? 2,
    split_phrases_text: (row?.split_phrases || []).join("\n"),
    enabled: row?.enabled ?? true,
  });

  const submit = () => {
    const payload = {
      name: draft.name.trim().toLowerCase(),
      label: draft.label.trim() || undefined,
      min_size: parseInt(draft.min_size || 0, 10),
      target_size: parseInt(draft.target_size || 0, 10),
      max_size: parseInt(draft.max_size || 0, 10),
      overlap: parseInt(draft.overlap || 0, 10),
      classifier_keywords: draft.classifier_keywords_text
        .split("\n").map((s) => s.trim()).filter(Boolean),
      classifier_min_matches: parseInt(draft.classifier_min_matches || 1, 10),
      split_phrases: draft.split_phrases_text
        .split("\n").map((s) => s.trim()).filter(Boolean),
      enabled: !!draft.enabled,
    };
    onSave(payload, isEdit);
  };

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="chunking-profile-dialog">
        <DialogHeader>
          <DialogTitle className="font-mono">
            {isEdit ? `Edit "${row.name}"` : "Add document type"}
          </DialogTitle>
          <DialogDescription className="text-xs font-mono">
            {isEdit && row.built_in
              ? "Built-in profile — name is locked, everything else can be edited or reset later."
              : "Create a new document type. Detection uses plain-English keywords; the system handles regex safely."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Name + label */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <div className="text-[10px] uppercase tracking-wider font-mono text-muted-foreground mb-1">
                Name (URL-safe)
              </div>
              <Input
                value={draft.name}
                onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                disabled={isEdit}
                placeholder="invoice"
                className="rounded-sm h-8 font-mono text-xs"
                data-testid="chunking-profile-dialog-name"
              />
              <div className="text-[10px] font-mono text-muted-foreground mt-0.5">
                lowercase letters/digits/_/-, ≤31 chars
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider font-mono text-muted-foreground mb-1">
                Label (display)
              </div>
              <Input
                value={draft.label}
                onChange={(e) => setDraft({ ...draft, label: e.target.value })}
                placeholder="Invoices & receipts"
                className="rounded-sm h-8 font-mono text-xs"
                data-testid="chunking-profile-dialog-label"
              />
            </div>
          </div>

          {/* Sizing */}
          <div>
            <div className="text-[10px] uppercase tracking-wider font-mono text-muted-foreground mb-1">
              Sizing (tokens)
            </div>
            <div className="grid grid-cols-4 gap-2">
              {["min_size", "target_size", "max_size", "overlap"].map((field) => (
                <div key={field}>
                  <div className="text-[10px] uppercase tracking-wider font-mono text-muted-foreground mb-1">
                    {field.replace("_", " ")}
                  </div>
                  <Input
                    type="number"
                    min={0}
                    value={draft[field]}
                    onChange={(e) =>
                      setDraft({ ...draft, [field]: parseInt(e.target.value || "0", 10) })
                    }
                    className="rounded-sm h-8 font-mono text-xs"
                    data-testid={`chunking-profile-dialog-${field}`}
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Detection */}
          <div>
            <div className="text-[10px] uppercase tracking-wider font-mono text-muted-foreground mb-1">
              Detect this type when document contains (one keyword per line)
            </div>
            <textarea
              value={draft.classifier_keywords_text}
              onChange={(e) => setDraft({ ...draft, classifier_keywords_text: e.target.value })}
              rows={6}
              placeholder={"INVOICE NUMBER\nBILL TO\nTOTAL DUE"}
              className="w-full rounded-sm border border-border bg-background px-2 py-1.5 font-mono text-xs"
              data-testid="chunking-profile-dialog-keywords"
            />
            <div className="text-[10px] font-mono text-muted-foreground mt-1 flex items-center justify-between">
              <span>
                Minimum matches needed:
                <Input
                  type="number"
                  min={1}
                  max={10}
                  value={draft.classifier_min_matches}
                  onChange={(e) =>
                    setDraft({ ...draft, classifier_min_matches: parseInt(e.target.value || "1", 10) })
                  }
                  className="inline-block w-14 ml-2 rounded-sm h-6 font-mono text-xs"
                  data-testid="chunking-profile-dialog-min-matches"
                />
              </span>
              <span className="text-muted-foreground/70">
                {draft.classifier_keywords_text.split("\n").filter((s) => s.trim()).length} keyword(s)
              </span>
            </div>
          </div>

          {/* Split phrases */}
          <div>
            <div className="text-[10px] uppercase tracking-wider font-mono text-muted-foreground mb-1">
              Start a new chunk at these phrases (optional, one per line)
            </div>
            <textarea
              value={draft.split_phrases_text}
              onChange={(e) => setDraft({ ...draft, split_phrases_text: e.target.value })}
              rows={3}
              placeholder={"INVOICE NUMBER\nLINE ITEM"}
              className="w-full rounded-sm border border-border bg-background px-2 py-1.5 font-mono text-xs"
              data-testid="chunking-profile-dialog-split-phrases"
            />
            <div className="text-[10px] font-mono text-muted-foreground mt-0.5">
              Leave empty if you don't need explicit split points.
            </div>
          </div>

          {/* Enabled */}
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={draft.enabled}
              onChange={(e) => setDraft({ ...draft, enabled: e.target.checked })}
              data-testid="chunking-profile-dialog-enabled"
            />
            <span className="text-xs font-mono">Enabled (used for classification)</span>
          </label>
        </div>

        <DialogFooter>
          <Button
            variant="ghost"
            onClick={onClose}
            className="h-8 rounded-sm text-xs"
            data-testid="chunking-profile-dialog-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={saving || !draft.name.trim()}
            className="h-8 rounded-sm text-xs"
            data-testid="chunking-profile-dialog-submit"
          >
            {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : (isEdit ? "Save changes" : "Create")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


/**
 * Phase 5.2 — Try Classifier preview dialog.
 *
 * Lets the admin paste a sample of text (a contract clause, a resume snippet,
 * etc.) and see WHICH profile would be picked + WHY (per-profile match counts)
 * without uploading a real file. Drives the [Test] button on every profile row
 * and the [Try classifier] button in the section header.
 */
function ChunkingProfilePreviewDialog({ hint, profiles, onClose }) {
  const [text, setText] = useState("");
  const [forceHint, setForceHint] = useState(hint || "");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    if (!text.trim()) {
      toast.error("Paste some text to test");
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const { data } = await api.post("/admin/chunking-profiles/preview", {
        text,
        profile_hint: forceHint || null,
      });
      setResult(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Preview failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="chunking-profile-preview-dialog">
        <DialogHeader>
          <DialogTitle className="font-mono">Try classifier</DialogTitle>
          <DialogDescription className="text-xs font-mono">
            Paste a sample (contract clause, resume snippet, etc.) → see which profile fires + how it would be chunked. Nothing is saved.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <div className="text-[10px] uppercase tracking-wider font-mono text-muted-foreground mb-1">
              Sample text
            </div>
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              rows={8}
              placeholder="Paste a snippet from a contract, resume, support article, transcript, etc."
              className="w-full rounded-sm border border-border bg-background px-2 py-1.5 font-mono text-xs"
              data-testid="chunking-preview-text"
            />
            <div className="text-[10px] font-mono text-muted-foreground mt-0.5">
              {text.length.toLocaleString()} chars
            </div>
          </div>

          <div className="flex items-end gap-2">
            <div className="flex-1">
              <div className="text-[10px] uppercase tracking-wider font-mono text-muted-foreground mb-1">
                Force profile (optional)
              </div>
              <Select value={forceHint || "_auto"} onValueChange={(v) => setForceHint(v === "_auto" ? "" : v)}>
                <SelectTrigger className="h-8 rounded-sm font-mono text-xs" data-testid="chunking-preview-force-hint">
                  <SelectValue placeholder="Auto-detect" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="_auto">Auto-detect</SelectItem>
                  {(profiles || []).map((p) => (
                    <SelectItem key={p.name} value={p.name}>
                      {p.name}{p.built_in ? " (built-in)" : " (custom)"}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button onClick={run} disabled={loading || !text.trim()} className="h-8 rounded-sm text-xs" data-testid="chunking-preview-run">
              {loading ? <Loader2 className="h-3 w-3 animate-spin" /> : "Run"}
            </Button>
          </div>

          {result && (
            <div className="space-y-3" data-testid="chunking-preview-result">
              <div className="border border-emerald-300 bg-emerald-50 dark:bg-emerald-950/30 rounded-sm p-3">
                <div className="text-[10px] uppercase tracking-wider font-mono text-emerald-700 dark:text-emerald-300">
                  Detected profile
                </div>
                <div className="font-display font-bold text-xl tracking-tight mt-1" data-testid="chunking-preview-detected">
                  {result.detected_profile}
                </div>
                <div className="text-[10px] font-mono text-muted-foreground mt-1">
                  Would produce {result.total_chunks} chunk{result.total_chunks === 1 ? "" : "s"} total
                </div>
              </div>

              <div>
                <div className="text-[10px] uppercase tracking-wider font-mono text-muted-foreground mb-1">
                  Match counts per profile
                </div>
                <div className="border border-border rounded-sm overflow-hidden">
                  <table className="w-full text-xs font-mono">
                    <tbody>
                      {(result.match_counts || []).map((m) => (
                        <tr key={m.name} className="border-b last:border-0 border-border">
                          <td className="p-2">{m.name}</td>
                          <td className="p-2 text-muted-foreground">{m.label}</td>
                          <td className="p-2 text-right">
                            {m.no_keywords ? (
                              <span className="text-muted-foreground">structural / no keywords</span>
                            ) : (
                              <span className={m.would_fire ? "text-emerald-700 dark:text-emerald-400 font-bold" : ""}>
                                {m.matches} / {m.min_matches} needed
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {Array.isArray(result.chunks_preview) && result.chunks_preview.length > 0 && (
                <div>
                  <div className="text-[10px] uppercase tracking-wider font-mono text-muted-foreground mb-1">
                    Chunks preview (first {result.chunks_preview.length} of {result.total_chunks})
                  </div>
                  <div className="space-y-2">
                    {result.chunks_preview.map((c) => (
                      <div key={c.index} className="border border-border rounded-sm p-2.5 bg-background">
                        <div className="flex items-center gap-2 text-[10px] font-mono text-muted-foreground mb-1 flex-wrap">
                          <span className="font-bold text-foreground">#{c.index}</span>
                          {c.block_type && <span className="px-1.5 py-0.5 border border-border rounded uppercase">{c.block_type}</span>}
                          {c.token_estimate != null && <span>{c.token_estimate} tok</span>}
                          {Array.isArray(c.headings) && c.headings.length > 0 && (
                            <span className="text-amber-700 dark:text-amber-300">[{c.headings.join(" › ")}]</span>
                          )}
                        </div>
                        <pre className="whitespace-pre-wrap text-[11px] font-mono text-foreground/90">{c.text}</pre>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} className="h-8 rounded-sm text-xs" data-testid="chunking-preview-close">
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}



// ===========================================================================
// Phase 7 (D3) — Admin-tunable provider overrides (timeout, polling, etc.)
// Per-provider settings card. Currently surfaces CloudConvert + Tensornex
// timeout knobs. New providers/fields show up automatically via the
// /admin/provider-overrides endpoint (driven by ADMIN_TUNABLE_FIELDS).
// ===========================================================================
function ProviderSettingsSection() {
  const [data, setData] = useState(null);       // {values, fields, spec_defaults}
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [drafts, setDrafts] = useState({});     // {provider: {field: stringInputValue}}
  const [savingProvider, setSavingProvider] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get("/admin/provider-overrides");
      setData(d);
      // Seed drafts from current values (string form for inputs).
      const seed = {};
      for (const prov of Object.keys(d.fields || {})) {
        seed[prov] = {};
        for (const field of Object.keys(d.fields[prov])) {
          const cur = (d.values || {})[prov]?.[field];
          seed[prov][field] = cur == null ? "" : String(cur);
        }
      }
      setDrafts(seed);
    } catch (e) {
      toast.error("Failed to load provider overrides");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const setField = (prov, field, raw) => {
    setDrafts((d) => ({ ...d, [prov]: { ...(d[prov] || {}), [field]: raw } }));
  };

  const save = async (prov) => {
    setSavingProvider(prov);
    try {
      // Build body — only include fields the user typed a value for.
      // Empty string means "reset to PROVIDER_SPECS default" — we omit it.
      const body = {};
      const fields = data?.fields?.[prov] || {};
      for (const f of Object.keys(fields)) {
        const v = (drafts[prov] || {})[f];
        if (v !== "" && v != null) body[f] = Number(v);
      }
      const { data: out } = await api.put(`/admin/provider-overrides/${prov}`, body);
      toast.success(`${prov} settings saved`);
      // Refresh local state to reflect persisted values.
      setData((d) => ({ ...d, values: { ...(d?.values || {}), [prov]: out.values } }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSavingProvider(null);
    }
  };

  const resetDefault = async (prov) => {
    setSavingProvider(prov);
    try {
      const { data: out } = await api.delete(`/admin/provider-overrides/${prov}`);
      toast.success(`${prov} reset to defaults`);
      setData((d) => ({ ...d, values: { ...(d?.values || {}), [prov]: out.values } }));
      // Clear local drafts so placeholder text shows again.
      const seed = {};
      for (const f of Object.keys(data?.fields?.[prov] || {})) seed[f] = "";
      setDrafts((d) => ({ ...d, [prov]: seed }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reset failed");
    } finally {
      setSavingProvider(null);
    }
  };

  const fmtField = (field) => {
    // Human-readable label + help text per field name.
    const map = {
      timeout_seconds: {
        label: "Max wait time",
        help: "How long to wait for a single extraction job before falling back. Big PDFs (>500 pages) commonly take 5–15 min. Set to 1800 (30 min) or higher if you process large gov/legal docs.",
        unit: "seconds",
      },
      poll_interval_seconds: {
        label: "Poll interval",
        help: "How often to check job status. Lower = noticed completion faster but more API quota. 3s is a good balance.",
        unit: "seconds",
      },
    };
    return map[field] || { label: field, help: "", unit: "" };
  };

  if (loading) {
    return (
      <div className="border-t border-border pt-8 mt-8 flex items-center gap-2 text-sm text-muted-foreground" data-testid="provider-settings-loading">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading provider settings…
      </div>
    );
  }
  if (!data || !Object.keys(data.fields || {}).length) return null;

  return (
    <div className="border-t border-border pt-8 mt-8" data-testid="provider-settings-section">
      <button
        className="flex items-center gap-2 text-left w-full hover:opacity-80 transition-opacity"
        onClick={() => setExpanded(!expanded)}
        data-testid="provider-settings-toggle"
      >
        {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        <Clock className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground font-mono">
            [ phase 7 — provider tuning ]
          </div>
          <h2 className="font-display font-black tracking-tight text-2xl mt-1">
            Provider Settings
          </h2>
        </div>
      </button>
      <p className="text-sm text-muted-foreground mt-2 max-w-2xl ml-6">
        Per-provider operational knobs (timeouts, polling) that can be tuned without code changes.
        Leave fields empty to use the platform default.
      </p>

      {expanded && (
        <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="provider-settings-cards">
          {Object.keys(data.fields).map((prov) => {
            const fields = data.fields[prov];
            const cur = (data.values || {})[prov] || {};
            const specDef = (data.spec_defaults || {})[prov] || {};
            const draft = drafts[prov] || {};
            const isSaving = savingProvider === prov;
            return (
              <div
                key={prov}
                className="border border-border rounded-sm p-4 bg-secondary/20 space-y-3"
                data-testid={`provider-card-${prov}`}
              >
                <div className="flex items-center justify-between">
                  <h3 className="font-mono text-sm font-bold uppercase tracking-wide">
                    {prov}
                  </h3>
                  <span className="text-[10px] font-mono text-muted-foreground">
                    {Object.keys(cur).length > 0 ? "custom" : "default"}
                  </span>
                </div>

                {Object.keys(fields).map((field) => {
                  const meta = fmtField(field);
                  const bounds = fields[field];
                  const defaultVal = specDef[field];
                  const draftVal = draft[field] ?? "";
                  return (
                    <div key={field} className="space-y-1.5" data-testid={`provider-field-${prov}-${field}`}>
                      <div className="flex items-center gap-1.5">
                        <label className="text-xs font-mono text-muted-foreground">
                          {meta.label}
                        </label>
                        <TooltipProvider delayDuration={150}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Info className="h-3 w-3 text-muted-foreground cursor-help" />
                            </TooltipTrigger>
                            <TooltipContent className="max-w-xs text-xs">
                              {meta.help} Range {bounds.min}–{bounds.max} {meta.unit}.
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </div>
                      <div className="flex items-center gap-2">
                        <Input
                          type="number"
                          min={bounds.min}
                          max={bounds.max}
                          value={draftVal}
                          onChange={(e) => setField(prov, field, e.target.value)}
                          placeholder={
                            defaultVal != null
                              ? `default: ${defaultVal}`
                              : `${bounds.min}–${bounds.max}`
                          }
                          className="rounded-sm font-mono text-sm"
                          data-testid={`provider-input-${prov}-${field}`}
                        />
                        <span className="text-xs text-muted-foreground whitespace-nowrap">
                          {meta.unit}
                        </span>
                      </div>
                      {defaultVal != null && (
                        <div className="text-[10px] font-mono text-muted-foreground/70">
                          platform default: {defaultVal}{meta.unit ? ` ${meta.unit}` : ""}
                          {defaultVal >= 60 && meta.unit === "seconds" && (
                            <span> (~{Math.round(defaultVal / 60)} min)</span>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}

                <div className="flex items-center gap-2 pt-2 border-t border-border">
                  <Button
                    onClick={() => save(prov)}
                    disabled={isSaving}
                    size="sm"
                    className="rounded-sm font-mono text-xs"
                    data-testid={`provider-save-${prov}`}
                  >
                    {isSaving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
                    Save
                  </Button>
                  <Button
                    onClick={() => resetDefault(prov)}
                    disabled={isSaving}
                    variant="outline"
                    size="sm"
                    className="rounded-sm font-mono text-xs"
                    data-testid={`provider-reset-${prov}`}
                  >
                    Reset to defaults
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}




function RoutingRulesSection() {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [simExt, setSimExt] = useState("pdf");
  const [simSizeKb, setSimSizeKb] = useState("1600");
  const [simPages, setSimPages] = useState("231");
  const [simResult, setSimResult] = useState(null);
  const [simulating, setSimulating] = useState(false);
  // Phase 2.5 — editor dialog + delete confirmation
  const [editingRule, setEditingRule] = useState(null);   // {} for new, {...row} for edit
  const [deletingRule, setDeletingRule] = useState(null);
  const [reorderingId, setReorderingId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/routing-rules");
      setRules(data.rules || []);
    } catch {
      toast.error("Failed to load routing rules");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, []);

  const patchRule = async (id, patch) => {
    setSavingId(id);
    try {
      const { data } = await api.put(`/admin/routing-rules/${id}`, patch);
      setRules((cur) => cur.map((r) => (r.id === id ? data : r)));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSavingId(null);
    }
  };

  // Phase 2.5 — reorder, create, delete handlers
  const moveRule = async (id, dir) => {
    const idx = rules.findIndex((r) => r.id === id);
    if (idx < 0) return;
    const neighbor = dir === "up" ? rules[idx - 1] : rules[idx + 1];
    if (!neighbor) return;
    setReorderingId(id);
    try {
      const { data } = await api.put("/admin/routing-rules/-/reorder", {
        order: [
          { id, priority: neighbor.priority },
          { id: neighbor.id, priority: rules[idx].priority },
        ],
      });
      setRules(data.rules || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reorder failed");
    } finally {
      setReorderingId(null);
    }
  };

  const handleSaveRule = async (form) => {
    try {
      if (form.id) {
        const { data } = await api.put(`/admin/routing-rules/${form.id}`, {
          name: form.name,
          priority: form.priority,
          conditions: form.conditions,
          primary: form.primary,
          speculative_fallback: form.speculative_fallback,
          enabled: form.enabled,
        });
        setRules((cur) => cur.map((r) => (r.id === form.id ? data : r)));
        toast.success("Rule updated");
      } else {
        const { data } = await api.post("/admin/routing-rules", {
          name: form.name,
          priority: form.priority,
          conditions: form.conditions,
          primary: form.primary,
          speculative_fallback: form.speculative_fallback,
          enabled: form.enabled,
        });
        setRules((cur) => [...cur, data].sort((a, b) => a.priority - b.priority));
        toast.success("Rule created");
      }
      setEditingRule(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const handleDeleteRule = async () => {
    if (!deletingRule) return;
    try {
      await api.delete(`/admin/routing-rules/${deletingRule.id}`);
      setRules((cur) => cur.filter((r) => r.id !== deletingRule.id));
      toast.success("Rule deleted");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    } finally {
      setDeletingRule(null);
    }
  };

  const runSimulator = async () => {
    setSimulating(true);
    try {
      const payload = { file_ext: simExt };
      if (simSizeKb) payload.size_kb = parseInt(simSizeKb, 10);
      if (simPages) payload.page_count = parseInt(simPages, 10);
      const { data } = await api.post("/admin/routing-rules/test", payload);
      setSimResult(data.matched);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Simulation failed");
    } finally {
      setSimulating(false);
    }
  };

  return (
    <div className="border-t border-border pt-8 mt-8" data-testid="routing-rules-section">
      <button
        className="flex items-center gap-2 text-left w-full hover:opacity-80 transition-opacity"
        onClick={() => setExpanded(!expanded)}
        data-testid="routing-rules-toggle"
      >
        {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        <Route className="h-4 w-4 text-sky-600 dark:text-sky-400" />
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground font-mono">
            [ phase 2 — file routing ]
          </div>
          <h2 className="font-display font-black tracking-tight text-2xl mt-1">
            Extraction Routing Rules
          </h2>
        </div>
      </button>
      <p className="text-sm text-muted-foreground mt-2 max-w-2xl ml-6">
        For each file uploaded, the engine picks the first matching rule (by priority) and
        runs its primary strategy. If the primary fails or returns empty, the optional
        fallback runs — never both at once (no parallel race, no double cost).
      </p>

      {expanded && (
        <div className="mt-6 space-y-6">
          {/* Phase 2.5 — Add rule button */}
          <div className="flex justify-end">
            <Button
              variant="outline"
              size="sm"
              className="rounded-sm font-mono text-xs"
              onClick={() =>
                setEditingRule({
                  name: "",
                  priority: (rules[rules.length - 1]?.priority ?? 100) + 10,
                  conditions: {
                    file_types: [],
                    min_pages: null,
                    max_pages: null,
                    min_size_kb: null,
                    max_size_kb: null,
                  },
                  primary: { provider: "tensornex", strategy: "docling" },
                  speculative_fallback: null,
                  enabled: true,
                })
              }
              data-testid="rule-add-btn"
            >
              <Plus className="h-3.5 w-3.5 mr-1.5" />
              Add rule
            </Button>
          </div>
          {loading ? (
            <div className="text-muted-foreground py-8 text-center">
              <Loader2 className="h-4 w-4 animate-spin inline-block mr-2" /> Loading…
            </div>
          ) : (
            <div className="space-y-px bg-border border border-border">
              {rules.map((r) => {
                const dirty = savingId === r.id;
                const c = r.conditions || {};
                const conditionPills = [];
                if (c.file_types?.length) conditionPills.push(c.file_types.join(", "));
                else conditionPills.push("any type");
                if (c.min_pages || c.max_pages) {
                  const lo = c.min_pages ?? "0";
                  const hi = c.max_pages ?? "∞";
                  conditionPills.push(`${lo}–${hi} pages`);
                }
                if (c.min_size_kb || c.max_size_kb) {
                  const lo = c.min_size_kb ?? "0";
                  const hi = c.max_size_kb ?? "∞";
                  conditionPills.push(`${lo}–${hi} KB`);
                }
                return (
                  <div
                    key={r.id}
                    className="bg-card p-5"
                    data-testid={`rule-row-${r.id}`}
                  >
                    <div className="grid grid-cols-12 gap-4 items-start">
                      <div className="col-span-12 lg:col-span-5">
                        <div className="flex items-center gap-2">
                          <span className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">
                            #{r.priority}
                          </span>
                          {!r.enabled && (
                            <span className="text-[10px] font-mono uppercase tracking-wider text-amber-500">
                              · disabled
                            </span>
                          )}
                        </div>
                        <div className="font-display font-bold tracking-tight mt-1">
                          {r.name}
                        </div>
                        <div className="flex flex-wrap gap-1.5 mt-2">
                          {conditionPills.map((p, i) => (
                            <span
                              key={i}
                              className="px-2 py-0.5 rounded-sm border border-border text-[10px] font-mono text-muted-foreground bg-muted/30"
                            >
                              {p}
                            </span>
                          ))}
                        </div>
                      </div>

                      <div className="col-span-12 lg:col-span-4">
                        <div className="text-[10px] uppercase tracking-wider font-mono text-muted-foreground mb-1.5">
                          Strategy
                        </div>
                        <div className="space-y-1.5">
                          <div className="font-mono text-xs">
                            <span className="text-muted-foreground">primary: </span>
                            <span className="text-emerald-700 dark:text-emerald-300 font-semibold">
                              {r.primary?.provider}
                              {r.primary?.model_id ? ` / ${r.primary.model_id}` : ` / ${r.primary?.strategy}`}
                            </span>
                          </div>
                          {r.speculative_fallback ? (
                            <div className="font-mono text-xs">
                              <span className="text-muted-foreground">fallback (on failure): </span>
                              <span className="text-purple-700 dark:text-purple-300 font-semibold">
                                {r.speculative_fallback.provider}
                                {r.speculative_fallback.model_id
                                  ? ` / ${r.speculative_fallback.model_id}`
                                  : ` / ${r.speculative_fallback.strategy}`}
                              </span>
                            </div>
                          ) : (
                            <div className="font-mono text-xs text-muted-foreground">
                              no fallback
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="col-span-12 lg:col-span-3 flex items-center justify-end gap-2">
                        <div className="flex items-center gap-0.5 mr-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 rounded-sm"
                            disabled={reorderingId === r.id || rules.findIndex((x) => x.id === r.id) === 0}
                            onClick={() => moveRule(r.id, "up")}
                            data-testid={`rule-up-${r.id}`}
                            title="Move up (decrease priority number)"
                          >
                            <ArrowUp className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 rounded-sm"
                            disabled={reorderingId === r.id || rules.findIndex((x) => x.id === r.id) === rules.length - 1}
                            onClick={() => moveRule(r.id, "down")}
                            data-testid={`rule-down-${r.id}`}
                            title="Move down (increase priority number)"
                          >
                            <ArrowDown className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 rounded-sm"
                            onClick={() => setEditingRule({ ...r })}
                            data-testid={`rule-edit-${r.id}`}
                            title="Edit rule"
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 rounded-sm hover:text-red-400"
                            onClick={() => setDeletingRule(r)}
                            data-testid={`rule-delete-${r.id}`}
                            title="Delete rule"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                        <Switch
                          checked={r.enabled}
                          onCheckedChange={(v) => patchRule(r.id, { enabled: v })}
                          data-testid={`rule-enabled-${r.id}`}
                        />
                        <span className="text-xs font-mono text-muted-foreground">
                          {r.enabled ? "ON" : "OFF"}
                        </span>
                        {(dirty || reorderingId === r.id) && (
                          <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* SIMULATOR */}
          <div className="bg-card border border-border p-5">
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground font-mono">
              [ simulator ]
            </div>
            <div className="font-display font-bold tracking-tight text-lg mt-1">
              Test which rule a file will match
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-3 mt-4">
              <div>
                <div className="text-[10px] uppercase tracking-wider font-mono text-muted-foreground mb-1.5">
                  File extension
                </div>
                <Input
                  value={simExt}
                  onChange={(e) => setSimExt(e.target.value)}
                  placeholder="pdf"
                  className="rounded-sm font-mono text-xs h-9"
                  data-testid="sim-ext-input"
                />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider font-mono text-muted-foreground mb-1.5">
                  Size (KB)
                </div>
                <Input
                  value={simSizeKb}
                  onChange={(e) => setSimSizeKb(e.target.value)}
                  placeholder="1600"
                  className="rounded-sm font-mono text-xs h-9"
                  data-testid="sim-size-input"
                />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider font-mono text-muted-foreground mb-1.5">
                  Pages
                </div>
                <Input
                  value={simPages}
                  onChange={(e) => setSimPages(e.target.value)}
                  placeholder="231"
                  className="rounded-sm font-mono text-xs h-9"
                  data-testid="sim-pages-input"
                />
              </div>
              <div className="flex items-end">
                <Button
                  onClick={runSimulator}
                  disabled={simulating || !simExt}
                  className="rounded-sm font-mono text-xs h-9 w-full"
                  data-testid="sim-run-btn"
                >
                  {simulating ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />
                  ) : (
                    <PlayCircle className="h-3.5 w-3.5 mr-1.5" />
                  )}
                  Simulate
                </Button>
              </div>
            </div>
            {simResult && (
              <div
                className="mt-4 p-3 rounded-sm border border-sky-300 bg-sky-50 dark:border-sky-700/60 dark:bg-sky-950/30"
                data-testid="sim-result"
              >
                <div className="text-[10px] uppercase tracking-wider font-mono text-sky-700 dark:text-sky-300 font-bold">
                  matched
                </div>
                <div className="font-display font-bold mt-0.5 text-foreground">{simResult.name}</div>
                <div className="text-xs font-mono text-muted-foreground mt-2">
                  primary:{" "}
                  <span className="text-emerald-700 dark:text-emerald-300 font-semibold">
                    {simResult.primary?.provider}
                    {simResult.primary?.model_id
                      ? ` / ${simResult.primary.model_id}`
                      : ` / ${simResult.primary?.strategy}`}
                  </span>
                </div>
                {simResult.speculative_fallback && (
                  <div className="text-xs font-mono text-muted-foreground">
                    fallback (on failure):{" "}
                    <span className="text-purple-700 dark:text-purple-300 font-semibold">
                      {simResult.speculative_fallback.provider}
                      {simResult.speculative_fallback.model_id
                        ? ` / ${simResult.speculative_fallback.model_id}`
                        : ` / ${simResult.speculative_fallback.strategy}`}
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ===== EDIT / CREATE RULE DIALOG ===== */}
      {editingRule && (
        <RuleEditorDialog
          rule={editingRule}
          onClose={() => setEditingRule(null)}
          onSave={handleSaveRule}
        />
      )}

      {/* ===== DELETE CONFIRMATION ===== */}
      <AlertDialog open={!!deletingRule} onOpenChange={(o) => !o && setDeletingRule(null)}>
        <AlertDialogContent className="rounded-sm">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete routing rule?</AlertDialogTitle>
            <AlertDialogDescription>
              <span className="font-mono text-foreground">{deletingRule?.name}</span> will be permanently removed.
              Files uploaded after deletion will fall through to the next matching rule (or the catch-all).
              This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm" data-testid="rule-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteRule}
              className="rounded-sm bg-red-600 hover:bg-red-700"
              data-testid="rule-delete-confirm"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}


/**
 * Phase 2.5 — Rule editor dialog. Form fields for name, priority, conditions,
 * primary strategy, optional fallback. Used for both create (rule.id absent)
 * and edit (rule.id present).
 *
 * Step 5 rewrite: provider+model are now picked from the live catalog (no
 * free-text typing). Strategy is auto-derived from provider via
 * STRATEGY_FOR_PROVIDER. The fallback runs sequentially ONLY if the
 * primary fails — no parallel race, no double billing.
 */
function RuleEditorDialog({ rule, onClose, onSave }) {
  const [form, setForm] = useState(() => ({
    ...rule,
    conditions: {
      file_types: rule.conditions?.file_types || [],
      min_pages: rule.conditions?.min_pages ?? null,
      max_pages: rule.conditions?.max_pages ?? null,
      min_size_kb: rule.conditions?.min_size_kb ?? null,
      max_size_kb: rule.conditions?.max_size_kb ?? null,
    },
    primary: rule.primary || { provider: "tensornex", strategy: "docling", model_id: null },
    speculative_fallback: rule.speculative_fallback || null,
  }));
  const [hasFallback, setHasFallback] = useState(!!rule.speculative_fallback);
  const [saving, setSaving] = useState(false);

  const fileTypesStr = (form.conditions.file_types || []).join(", ");

  const save = async () => {
    setSaving(true);
    try {
      // Auto-derive strategy from provider for both primary + fallback.
      const primaryProvider = form.primary?.provider;
      const primaryWithStrategy = primaryProvider
        ? {
            provider: primaryProvider,
            strategy: STRATEGY_FOR_PROVIDER[primaryProvider] || form.primary?.strategy || "docling",
            model_id: form.primary?.model_id || null,
          }
        : form.primary;

      let fallbackPayload = null;
      if (hasFallback && form.speculative_fallback?.provider) {
        const fbProvider = form.speculative_fallback.provider;
        fallbackPayload = {
          provider: fbProvider,
          strategy: STRATEGY_FOR_PROVIDER[fbProvider] || form.speculative_fallback?.strategy || "docling",
          model_id: form.speculative_fallback?.model_id || null,
          // Kept for back-compat with the schema; ignored by the new
          // sequential-fallback runtime.
          trigger_after_seconds: null,
        };
      }

      const payload = {
        ...form,
        primary: primaryWithStrategy,
        speculative_fallback: fallbackPayload,
      };
      await onSave(payload);
    } finally {
      setSaving(false);
    }
  };

  const setCond = (k, v) => setForm((f) => ({ ...f, conditions: { ...f.conditions, [k]: v } }));
  // When the model picker changes provider, we also reset the model_id so the
  // admin is forced to pick a valid pairing.
  const setPrimaryPick = ({ provider, model_id }) =>
    setForm((f) => ({
      ...f,
      primary: { provider, model_id, strategy: STRATEGY_FOR_PROVIDER[provider] || "docling" },
    }));
  const setFallbackPick = ({ provider, model_id }) =>
    setForm((f) => ({
      ...f,
      speculative_fallback: {
        ...(f.speculative_fallback || {}),
        provider,
        model_id,
        strategy: STRATEGY_FOR_PROVIDER[provider] || "docling",
      },
    }));
  const parseIntOrNull = (s) => {
    const n = parseInt(s, 10);
    return Number.isFinite(n) ? n : null;
  };

  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-sm max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="rule-editor-dialog">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight">
            {form.id ? "Edit routing rule" : "New routing rule"}
          </DialogTitle>
          <DialogDescription>
            Matches files by type / size / page count, then runs the primary
            strategy. If the primary fails or returns empty, the optional
            fallback runs — never both at the same time (no parallel race, no double cost).
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-2">
          {/* Name + priority */}
          <div className="grid grid-cols-12 gap-3">
            <div className="col-span-8">
              <div className="text-[10px] uppercase tracking-wider font-mono text-muted-foreground mb-1.5">
                Name
              </div>
              <Input
                value={form.name || ""}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="e.g. Big PDF → OpenRouter Gemini Pro"
                className="rounded-sm font-mono text-xs h-9"
                data-testid="rule-form-name"
              />
            </div>
            <div className="col-span-4">
              <div className="text-[10px] uppercase tracking-wider font-mono text-muted-foreground mb-1.5">
                Priority (lower = first)
              </div>
              <Input
                type="number"
                value={form.priority ?? ""}
                onChange={(e) => setForm((f) => ({ ...f, priority: parseInt(e.target.value, 10) || 0 }))}
                className="rounded-sm font-mono text-xs h-9"
                data-testid="rule-form-priority"
              />
            </div>
          </div>

          {/* Conditions */}
          <div className="border border-border rounded-sm p-3 space-y-3 bg-muted/20">
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground font-mono">
              conditions
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider font-mono text-muted-foreground mb-1.5">
                File types (comma-separated; blank = any)
              </div>
              <Input
                value={fileTypesStr}
                onChange={(e) =>
                  setCond(
                    "file_types",
                    e.target.value
                      .split(",")
                      .map((s) => s.trim().toLowerCase())
                      .filter(Boolean),
                  )
                }
                placeholder="pdf, docx, pptx"
                className="rounded-sm font-mono text-xs h-9"
                data-testid="rule-form-file-types"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <div className="text-[10px] uppercase tracking-wider font-mono text-muted-foreground mb-1.5">
                  Min pages
                </div>
                <Input
                  type="number"
                  value={form.conditions.min_pages ?? ""}
                  onChange={(e) => setCond("min_pages", parseIntOrNull(e.target.value))}
                  className="rounded-sm font-mono text-xs h-9"
                  data-testid="rule-form-min-pages"
                />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider font-mono text-muted-foreground mb-1.5">
                  Max pages
                </div>
                <Input
                  type="number"
                  value={form.conditions.max_pages ?? ""}
                  onChange={(e) => setCond("max_pages", parseIntOrNull(e.target.value))}
                  className="rounded-sm font-mono text-xs h-9"
                  data-testid="rule-form-max-pages"
                />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider font-mono text-muted-foreground mb-1.5">
                  Min size (KB)
                </div>
                <Input
                  type="number"
                  value={form.conditions.min_size_kb ?? ""}
                  onChange={(e) => setCond("min_size_kb", parseIntOrNull(e.target.value))}
                  className="rounded-sm font-mono text-xs h-9"
                  data-testid="rule-form-min-size"
                />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider font-mono text-muted-foreground mb-1.5">
                  Max size (KB)
                </div>
                <Input
                  type="number"
                  value={form.conditions.max_size_kb ?? ""}
                  onChange={(e) => setCond("max_size_kb", parseIntOrNull(e.target.value))}
                  className="rounded-sm font-mono text-xs h-9"
                  data-testid="rule-form-max-size"
                />
              </div>
            </div>
          </div>

          {/* Primary strategy — live model picker, no free text */}
          <div className="border border-emerald-300 dark:border-emerald-700/40 rounded-sm p-3 space-y-3 bg-emerald-50 dark:bg-emerald-950/10">
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-emerald-700 dark:text-emerald-400 font-mono">
              primary — runs first
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider font-mono text-muted-foreground mb-1.5">
                Provider / model
              </div>
              <ModelPicker
                value={form.primary}
                onChange={setPrimaryPick}
                data-testid="rule-form-primary-picker"
              />
              <div className="text-[10px] font-mono text-muted-foreground mt-1">
                strategy auto: {STRATEGY_FOR_PROVIDER[form.primary?.provider] || "—"}
              </div>
            </div>
          </div>

          {/* Fallback — runs ONLY if primary fails or returns empty */}
          <div className="border border-purple-300 dark:border-purple-700/40 rounded-sm p-3 space-y-3 bg-purple-50 dark:bg-purple-950/10">
            <div className="flex items-center justify-between">
              <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-purple-700 dark:text-purple-400 font-mono">
                fallback (runs ONLY if primary fails)
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={hasFallback}
                  onCheckedChange={setHasFallback}
                  data-testid="rule-form-fallback-toggle"
                />
                <span className="text-xs font-mono text-muted-foreground">
                  {hasFallback ? "ON" : "OFF"}
                </span>
              </div>
            </div>
            {hasFallback && (
              <div>
                <div className="text-[10px] uppercase tracking-wider font-mono text-muted-foreground mb-1.5">
                  Provider / model
                </div>
                <ModelPicker
                  value={form.speculative_fallback}
                  onChange={setFallbackPick}
                  data-testid="rule-form-fallback-picker"
                  fallback
                />
                <div className="text-[10px] font-mono text-muted-foreground mt-1">
                  strategy auto: {STRATEGY_FOR_PROVIDER[form.speculative_fallback?.provider] || "—"}
                </div>
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-sm" data-testid="rule-form-cancel">
            Cancel
          </Button>
          <Button onClick={save} disabled={saving || !form.name} className="rounded-sm" data-testid="rule-form-save">
            {saving && <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />}
            {form.id ? "Save" : "Create"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}



/**
 * Cheat-sheet panel — collapsible reference of every operation's purpose.
 * Surfaced at the top of the Operational Models tab so the admin never has
 * to leave the page to remember what each slug does.
 */
function CheatSheetPanel({ ops }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-border rounded-sm bg-card" data-testid="cheat-sheet-panel">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full px-4 py-3 flex items-center justify-between gap-2 hover:bg-muted/40 transition-colors text-left"
        data-testid="cheat-sheet-toggle"
      >
        <div className="flex items-center gap-2">
          <BookOpen className="h-4 w-4 text-sky-600 dark:text-sky-400" />
          <span className="font-display font-bold tracking-tight">
            What does each operation do?
          </span>
          <span className="text-xs font-mono text-muted-foreground">
            · quick reference
          </span>
        </div>
        {open ? (
          <ChevronUp className="h-4 w-4 text-muted-foreground" />
        ) : (
          <ChevronDown className="h-4 w-4 text-muted-foreground" />
        )}
      </button>
      {open && (
        <div className="border-t border-border px-4 py-4 space-y-3" data-testid="cheat-sheet-body">
          {(ops || []).map((op) => {
            const info = OPERATION_INFO[op.operation_slug];
            if (!info) return null;
            const cat = CATEGORY_META[op.category] || CATEGORY_META.generative;
            return (
              <div
                key={op.operation_slug}
                className="grid grid-cols-12 gap-3 items-start py-2 border-b border-border last:border-b-0"
              >
                <div className="col-span-12 sm:col-span-4">
                  <div className={`text-[10px] uppercase tracking-wider font-mono ${cat.color}`}>
                    {cat.label}
                  </div>
                  <div className="font-display font-bold tracking-tight text-sm mt-0.5">
                    {op.label}
                  </div>
                  <div className="text-[10px] font-mono text-muted-foreground">
                    {op.operation_slug}
                  </div>
                </div>
                <div className="col-span-12 sm:col-span-8">
                  <div className="text-sm text-foreground font-semibold">{info.short}</div>
                  <div className="text-xs text-muted-foreground mt-1 leading-relaxed">
                    {info.long}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
